import * as React from 'react';
import { ComponentType } from 'react';

import { observer, use$, useObservable, useObserve } from '@legendapp/state/react';
import styled from 'styled-components';

import { degreeImageStore, degreeImageStoreActions, rootStore, zoomStore } from '../../store';
import { testId } from '../../test-utils/constants';

interface ImageComponentProps {
	degree: number;
	currentDegree: number;
	scale: number;
}

const DegreeImageComponent = () => {
	const currentDegree = use$(degreeImageStore.currentDegree) || 0;
	const zoomLevel = use$(zoomStore.zoomLevel) || 0;

	const images = useObservable<
		Array<{
			Component: ComponentType<ImageComponentProps>;
			degree: number;
		}>
	>([]);

	useObserve(() => {
		async function loadImages() {
			const [_, currentVariantAssets, currentVariantBaseUrl] = rootStore.currentVariant.get();
			const imagePromises = currentVariantAssets.map((url: string, index: number) => {
				// We have 36 images so..
				const degree = index * 10;

				return new Promise<{ Component: ComponentType<ImageComponentProps>; degree: number }>(resolve => {
					const img = new Image();
					const imgUrl = currentVariantBaseUrl + url;

					img.src = imgUrl;
					img.srcset = imgUrl;

					img.onload = () => {
						const DegreeImageComponent = observer(({ currentDegree, scale }: ImageComponentProps) => (
							<DegreeImage
								data-testid={testId(`Degree_Image_${degree}`)}
								src={imgUrl}
								srcSet={imgUrl}
								hidden={degree !== currentDegree}
								scale={scale}
								decoding="sync"
								draggable={false}
							/>
						));
						resolve({ Component: DegreeImageComponent, degree });
					};
				});
			});

			const loadingSpinnerTimer = setTimeout(() => degreeImageStoreActions.setIsImageLoaded(true), 3000);

			const loadedImages = await Promise.all(imagePromises);
			images.set(loadedImages);

			degreeImageStoreActions.setIsImageLoaded(false);
			clearTimeout(loadingSpinnerTimer);
		}

		loadImages();
	});

	return (
		<>
			{use$(images).map(({ Component, degree }) => (
				<Component
					key={`item-${degree}`}
					degree={degree}
					currentDegree={currentDegree}
					scale={zoomLevel ? zoomLevel : 1}
				/>
			))}
		</>
	);
};

const DegreeImage = styled.img<{
	scale?: number;
}>`
	width: 100%;
	height: 100%;
	user-select: none;
	object-fit: cover;
	transition: all 0.5s linear;
	transform: scale(${props => props.scale || 1});
`;

export default DegreeImageComponent;
